import PropTypes from 'prop-types'
import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { I18n, Translate } from 'react-redux-i18n'
import { Cell, Customized } from 'recharts'
import { ChartContainer } from '../../../../../common/chart/chartContainer'
import Bar from '../../../../../common/chart/chartContainer/Bar'
import { Element } from '../../../../../common/html/element'
import { SCREEN } from '../../../../../common/selectCurrencyUnit/constant'
import { useFormatCurrency } from '../../../../../common/selectCurrencyUnit/useFormatCurrency'
import { Tabs } from '../../../../../common/tabs'
import { getAppendStyleItemWithCustom } from '../../../../../common/tabs/helper'
import { formatByTheme } from '../../../../../helpers/theme'
import {
  convertData,
  formatVal,
  isInValidValue,
  valToPercent,
} from '../../../../../helpers/value'
import { EMPTY_VALUE } from '../../../../../utils/common'
import {
  getNoDataComponent,
  getStyleHeightContainer,
  getYearTabs,
  handleIconDownload,
} from '../../helper'
import classes from '../../index.module.scss'
import { changeYear, selectYear } from '../../store/slice'
import {
  selectDataViewByEachYear,
  selectDataViewByTrend,
  selectIndicatorById,
  selectYears,
} from './store/slice'

const netRevenueValueId = 'FA165'
const netRevenuePercentageId = 'FA151'

const idsBar = ['FA151', 'RT0050', 'RT0051', 'RT0052', 'RT0054']

const idAverageById = {
  RT0050: 'RTI0050',
  RT0051: 'RTI0051',
  RT0052: 'RTI0052',
  RT0054: 'RTI0054',
}

const color = ['#006AA6', '#C06C84', '#74B49A', '#7F63B9', '#FFCD60']

const BAR_SIZE = 30

const EachYear = ({
  id,
  isDynamicReport,
  year: yearSetting,
  isDownloadComponent,
}) => {
  const dispatch = useDispatch()

  const { getExchangeRate, multiplier } = useFormatCurrency(
    SCREEN.FINANCIAL_ANALYSIS,
  )
  const indicatorById = useSelector(selectIndicatorById)
  const year = useSelector(selectYear(id, yearSetting))
  const data = useSelector(selectDataViewByEachYear(year))
  const dataByTrend = useSelector(selectDataViewByTrend)
  const locale = useSelector((state) => state.i18n.locale)
  const years = useSelector(selectYears)

  const tabs = getYearTabs(years)

  const netRevenueValue = dataByTrend.find((item) => item.year === +year)?.[
    netRevenueValueId
  ]
  const netRevenueAvgRatioName =
    Object.values(indicatorById)[0]?.ratioName || ''

  useEffect(() => {
    if (
      (tabs.length && !year) ||
      !tabs.map((item) => item.key).includes(String(year))
    ) {
      setYear(tabs[tabs.length - 1]?.key)
    }
  }, [tabs.length, year])

  const setYear = (year) => dispatch(changeYear({ id, year }))

  const dataConvert = convertData(
    data
      .filter((item) => idsBar.includes(item.id))
      .map((item) => {
        const value = !isInValidValue(item.value)
          ? item.value * netRevenueValue
          : undefined

        const idAverage = idAverageById[item.id]

        const origValue = item.value

        let valueAverage = data.find((item) => item.id === idAverage)?.value

        valueAverage = !isInValidValue(valueAverage)
          ? valueAverage * netRevenueValue
          : undefined

        return {
          ...item,
          percentRealValue: origValue,
          year,
          percentDisplay: Math.min(
            ((Math.abs(value) || 0) / netRevenueValue) * 100,
            100,
          ),
          ratioName: indicatorById[item.id]?.ratioName || '',
          value,
          valueAverage,
          percentAverage: Math.min(
            ((Math.abs(valueAverage) || 0) / netRevenueValue) * 100,
            100,
          ),
        }
      }),
    getExchangeRate,
    indicatorById,
    multiplier,
    ['id', 'ratioName', 'percentDisplay', 'percentAverage'],
    ['percentRealValue'],
  )

  const dataContainNetValueOnly =
    dataConvert?.length === 1 && dataConvert[0]?.id === netRevenuePercentageId

  const isNoData = !dataConvert.length || dataContainNetValueOnly

  useEffect(() => {
    handleIconDownload(id, isNoData)
  }, [isNoData])

  const CustomizedLabel = (props) => {
    const locale = useSelector((state) => state.i18n.locale)
    let { x, y, width, height, id, data, value } = props

    const realLabelVal =
      data?.find((item) => item.id === id)?.percentRealValue || ''

    y += isDownloadComponent || isDynamicReport ? 9 : 0
    x += (isDownloadComponent || isDynamicReport) && value >= 80 ? width / 2 : 0

    if (value >= 80) {
      return (
        <g style={{ pointerEvents: 'none' }}>
          <foreignObject
            x={x}
            y={y}
            height={height}
            width={width}
            color="#FFFFFF"
          >
            <Element
              className="h100 w100 dFlex alignItemsCenter justifyContentCenter"
              style={{ color: '#FFFFFF' }}
            >
              {valToPercent(
                realLabelVal,
                locale,
                true,
                undefined,
                realLabelVal === 100 ? 0 : 2,
              )}
            </Element>
          </foreignObject>
        </g>
      )
    }

    const valuePercent = valToPercent(realLabelVal, locale, true)

    return (
      <g>
        <foreignObject
          x={x + (valuePercent !== EMPTY_VALUE ? width : 0) + 3}
          y={y}
          height={height}
          width={200}
        >
          <Element
            className="h100 w100 dFlex alignItemsCenter"
            style={{ color: '#3A414B' }}
          >
            {valuePercent}
          </Element>
        </foreignObject>
      </g>
    )
  }

  return (
    <div
      className="flexColumn"
      style={{
        height: getStyleHeightContainer(isDynamicReport, isNoData, 332),
      }}
    >
      <Element
        style={{
          borderBottom: !isDynamicReport ? '1px solid #B5CEF8' : undefined,
        }}
        className={`dFlex justifyContentEnd ${classes.tabContainer}`}
      >
        {!!tabs.length && (
          <>
            {!isDynamicReport ? (
              <Tabs
                activeKey={year}
                items={tabs}
                tabListClassName={classes.tabs}
                getAppendStyleItem={getAppendStyleItemWithCustom()}
                isScrollContent={false}
                onChange={setYear}
              />
            ) : (
              <div className="fw600 py1 w100">
                <Translate value="common.FISCAL_YEAR" />: {year}
              </div>
            )}
          </>
        )}
      </Element>
      <div className="h100 mt1">
        {isNoData || isInValidValue(netRevenueValue) ? (
          getNoDataComponent(isDynamicReport)
        ) : (
          <ChartContainer
            data={dataConvert}
            margin={{
              top: 0,
              bottom: 0,
              left: 10,
              right: 10,
            }}
            width="100%"
            height="100%"
            layout="vertical"
            cartesianGrid={{
              vertical: true,
              horizontal: false,
              stroke: formatByTheme('#E5E5E8'),
            }}
            yAxis={[
              {
                dataKey: 'ratioName',
                yAxisId: 'left',
                type: 'category',
                tickStyle: { color: '#3A414B' },
                lineClamp: 2,
              },
            ]}
            xAxis={[
              {
                type: 'number',
                dataKeys: ['value'],
                ticks: [0, 20, 40, 60, 80, 100],
                tickFormatter: (val) => formatVal(val, locale) + '%',
                axisLine: false,
                tick: { fill: '#9C9C9C' },
              },
            ]}
            tooltip={{
              schema: {
                title: {
                  hide: true,
                },
                content: [
                  {
                    dataKey: 'percentRealValue',
                    formatValue: (value) => valToPercent(value, locale, true),
                  },
                  {
                    dataKey: 'percentAverage',
                    formatLabel: (label) =>
                      label + '_' + I18n.t('financialAnalysis.INDUSTRY_AVG'),
                    formatValue: (value) => valToPercent(value, locale, true),
                    isHidden: (data) =>
                      data.ratioName === netRevenueAvgRatioName,
                  },
                ],
              },
            }}
          >
            <Bar
              yAxisId="left"
              dataKey="percentDisplay"
              barSize={BAR_SIZE}
              label={<CustomizedLabel data={dataConvert} />}
            >
              {dataConvert.map((item, index) => {
                return (
                  <Cell
                    key={`cell-${index}`}
                    fill={item.value < 0 ? '#FF596A' : color[index]}
                  />
                )
              })}
            </Bar>
            <Customized component={CustomLine} yAxisId="left" />
          </ChartContainer>
        )}
      </div>
    </div>
  )
}

EachYear.propTypes = {
  id: PropTypes.string,
}

const CustomLine = (props) => {
  const { data, tooltipTicks, xAxisMap } = props
  const scale = xAxisMap[0].scale

  const lines = tooltipTicks.map((item) => {
    const xCoordinate = scale(data[item.index].percentAverage)
    const isDisplay = !!data[item.index].percentAverage
    return {
      isDisplay,
      x1: xCoordinate,
      x2: xCoordinate,
      y1: item.coordinate - BAR_SIZE / 2 - 5,
      y2: item.coordinate + BAR_SIZE / 2 + 5,
    }
  })

  return (
    <g>
      {lines.map((item) => {
        if (!item.isDisplay) {
          return undefined
        }

        delete item.isDisplay

        return (
          <line
            key={item.y1}
            {...item}
            stroke={formatByTheme('#9C9C9C')}
            strokeWidth={1}
          />
        )
      })}
    </g>
  )
}

export default EachYear
